import { useState, useEffect, useCallback, useRef } from "react";
import * as tf from "@tensorflow/tfjs";

const MODEL_KEY  = "indexeddb://digitair-anomaly-model";
const VOCAB_KEY  = "digitair-anomaly-vocab";
const THRESH_KEY = "digitair-anomaly-threshold";
export const ANOMALY_EPOCHS = 60;

function buildFeature(record, vocab) {
  const { depPorts, arrPorts, acTypes } = vocab;
  const depIdx = depPorts.indexOf(record.dep);
  const arrIdx = arrPorts.indexOf(record.arr);
  const acIdx  = acTypes.indexOf(record.acType);
  return [
    record.dayOfWeek / 6,
    record.month     / 11,
    record.stdHour   / 23,
    record.stdMinute / 59,
    depIdx < 0 ? 0 : depIdx / Math.max(depPorts.length - 1, 1),
    arrIdx < 0 ? 0 : arrIdx / Math.max(arrPorts.length - 1, 1),
    acIdx  < 0 ? 0 : acIdx  / Math.max(acTypes.length  - 1, 1),
  ];
}

function buildAutoencoder() {
  const input      = tf.input({ shape: [7] });
  const enc1       = tf.layers.dense({ units: 5, activation: "relu" }).apply(input);
  const bottleneck = tf.layers.dense({ units: 3, activation: "relu" }).apply(enc1);
  const dec1       = tf.layers.dense({ units: 5, activation: "relu" }).apply(bottleneck);
  const output     = tf.layers.dense({ units: 7, activation: "sigmoid" }).apply(dec1);
  const model      = tf.model({ inputs: input, outputs: output });
  model.compile({ optimizer: tf.train.adam(0.005), loss: "meanSquaredError" });
  return model;
}

export function useAnomalyModel() {
  const [model, setModel]               = useState(null);
  const [isModelReady, setIsModelReady] = useState(false);
  const [isTraining, setIsTraining]     = useState(false);
  const [trainingLogs, setTrainingLogs] = useState([]);
  const [vocab, setVocab]               = useState(null);
  const [threshold, setThreshold]       = useState(null);
  const workerRef                       = useRef(null);

  useEffect(() => {
    const loadSaved = async () => {
      try {
        const savedVocab  = localStorage.getItem(VOCAB_KEY);
        const savedThresh = localStorage.getItem(THRESH_KEY);
        if (!savedVocab) return;
        const m = await tf.loadLayersModel(MODEL_KEY);
        setModel(m);
        setVocab(JSON.parse(savedVocab));
        if (savedThresh) setThreshold(parseFloat(savedThresh));
        setIsModelReady(true);
      } catch (err) {
        console.warn("Anomaly model: failed to load from storage:", err);
      }
    };
    loadSaved();
    return () => { workerRef.current?.terminate(); };
  }, []);

  const trainModel = useCallback(async (rawData) => {
    if (!rawData || rawData.length < 10) return false;
    setIsTraining(true);
    setTrainingLogs([]);

    const depPorts = [...new Set(rawData.map((r) => r.dep))].sort();
    const arrPorts = [...new Set(rawData.map((r) => r.arr))].sort();
    const acTypes  = [...new Set(rawData.map((r) => r.acType))].sort();
    const newVocab = { depPorts, arrPorts, acTypes };

    const features = rawData.map((r) => buildFeature(r, newVocab));

    return new Promise((resolve) => {
      workerRef.current?.terminate();
      // eslint-disable-next-line no-undef
      const worker = new Worker(new URL('./workers/tfTrainWorker.js', import.meta.url));
      workerRef.current = worker;

      worker.onmessage = async ({ data: msg }) => {
        if (msg.type === 'epoch') {
          setTrainingLogs((prev) => [...prev, msg.payload]);
          return;
        }
        worker.terminate();
        workerRef.current = null;
        setIsTraining(false);

        if (msg.type === 'error') {
          console.error("Anomaly model worker error:", msg.payload.message);
          resolve(false);
          return;
        }

        const { weightSpecs, threshold: thresh } = msg.payload;
        const m       = buildAutoencoder();
        const weights = weightSpecs.map(({ data, shape, dtype }) =>
          tf.tensor(data, shape, dtype)
        );
        m.setWeights(weights);
        weights.forEach((w) => w.dispose());

        setVocab(newVocab);
        setModel(m);
        setThreshold(thresh);
        setIsModelReady(true);

        let saved = false;
        try {
          localStorage.removeItem(VOCAB_KEY);
          localStorage.removeItem(THRESH_KEY);
          try { await tf.io.removeModel(MODEL_KEY); } catch {}
          await m.save(MODEL_KEY);
          localStorage.setItem(VOCAB_KEY, JSON.stringify(newVocab));
          localStorage.setItem(THRESH_KEY, String(thresh));
          saved = true;
        } catch (err) {
          console.error("Anomaly model: save failed:", err);
        }
        resolve(saved);
      };

      worker.onerror = (err) => {
        console.error("Anomaly model worker crash:", err);
        worker.terminate();
        workerRef.current = null;
        setIsTraining(false);
        resolve(false);
      };

      // Anomaly model trains xs → xs (autoencoder), no separate labels needed
      worker.postMessage({
        type: 'train',
        payload: { modelType: 'anomaly', features, epochs: ANOMALY_EPOCHS, batchSize: 32, validationSplit: 0.1 },
      });
    });
  }, []);

  const predict = useCallback(
    (flights) => {
      if (!model || !vocab || !flights.length) return null;

      const features = flights.map((f) => {
        const d        = new Date(f.flightDate);
        const [h, min] = (f.std || "0:0").split(":").map(Number);
        return buildFeature({
          dep:       f.dep    || "",
          arr:       f.arr    || "",
          acType:    f.acType || "",
          dayOfWeek: isNaN(d)   ? 0 : d.getDay(),
          month:     isNaN(d)   ? 0 : d.getMonth(),
          stdHour:   isNaN(h)   ? 0 : h,
          stdMinute: isNaN(min) ? 0 : min,
        }, vocab);
      });

      const xs     = tf.tensor2d(features);
      const recon  = tf.tidy(() => model.predict(xs));
      const errors = tf.tidy(() => tf.mean(tf.square(tf.sub(xs, recon)), 1));
      const result = Array.from(errors.dataSync());
      errors.dispose();
      recon.dispose();
      xs.dispose();
      return result;
    },
    [model, vocab]
  );

  const resetModel = useCallback(async () => {
    workerRef.current?.terminate();
    workerRef.current = null;
    try { await tf.io.removeModel(MODEL_KEY); } catch {}
    localStorage.removeItem(VOCAB_KEY);
    localStorage.removeItem(THRESH_KEY);
    setModel(null);
    setVocab(null);
    setThreshold(null);
    setIsModelReady(false);
    setIsTraining(false);
    setTrainingLogs([]);
  }, []);

  return {
    isModelReady,
    isTraining,
    trainingLogs,
    vocab,
    threshold,
    trainModel,
    predict,
    resetModel,
  };
}
